// Log an Uber booked for a driver or an owner.
//
// The team member books it in the Uber app first; this is the record. Choosing
// a driver fills in the car they last drove, and choosing an owner offers only
// their own cars. Who carries the cost is chosen per ride, and anyone but
// Fleetly needs a reason. Recording it charges nobody: db/migrations/5600 says
// why. Postgres checks every field and writes the refusal the operator reads.
import { useEffect, useMemo, useState } from 'react'
import { Route } from 'lucide-react'
import { Button } from '../../lib/shadcn/button'
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '../../lib/shadcn/dialog'
import { Input } from '../../lib/shadcn/input'
import { NativeSelect } from '../../lib/shadcn/native-select'
import { Spinner } from '../../lib/shadcn/spinner'
import { useLogRide } from '../../hooks/backend/rides'
import type { CarriedBy, RideOptions, RiderKind } from '../../utils/rides'

type Saved = { ok: boolean; id: string; ref: string }

export function LogRideDialog({
  open, onOpenChange, options, onSaved,
}: {
  open: boolean
  onOpenChange: (o: boolean) => void
  options: RideOptions | null
  onSaved: () => void
}) {
  const [kind, setKind] = useState<RiderKind>('driver')
  const [driverId, setDriverId] = useState('')
  const [ownerId, setOwnerId] = useState('')
  const [vehicleId, setVehicleId] = useState('')
  const [reasonKind, setReasonKind] = useState('')
  const [reason, setReason] = useState('')
  const [pickup, setPickup] = useState('')
  const [dropoff, setDropoff] = useState('')
  const [rideAt, setRideAt] = useState('')
  const [amount, setAmount] = useState('')
  const [tripRef, setTripRef] = useState('')
  const [carriedBy, setCarriedBy] = useState<CarriedBy>('fleetly')
  const [carriedNote, setCarriedNote] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [saved, setSaved] = useState<Saved | null>(null)
  const create = useLogRide<Saved>()

  // Reset only when the dialog opens, never when the options refetch on focus,
  // or what the operator typed would be wiped (the Academy dialog, 30 September).
  useEffect(() => {
    if (!open) return
    setKind('driver'); setDriverId(''); setOwnerId(''); setVehicleId('')
    setReasonKind(''); setReason(''); setPickup(''); setDropoff('')
    setRideAt(options?.now ?? ''); setAmount(''); setTripRef('')
    setCarriedBy('fleetly'); setCarriedNote('')
    setError(null); setSaved(null)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open])

  const drivers = options?.drivers ?? []
  const owners = options?.owners ?? []
  const allVehicles = options?.vehicles ?? []
  const owner = owners.find((o) => o.id === ownerId)
  // An owner's ride is about their own car, so offer only those.
  const vehicles = useMemo(
    () => (kind === 'owner' && owner ? allVehicles.filter((v) => owner.vehicle_ids.includes(v.id)) : allVehicles),
    [kind, owner, allVehicles],
  )
  const reasons = (options?.reasons ?? []).filter((r) => r.rider === null || r.rider === kind)
  const chosenCar = allVehicles.find((v) => v.id === vehicleId)
  const carOwnerName = chosenCar?.owner_id ? owners.find((o) => o.id === chosenCar.owner_id)?.name ?? null : null

  function chooseKind(k: RiderKind) {
    setKind(k); setDriverId(''); setOwnerId(''); setVehicleId('')
    if (reasonKind && !(options?.reasons ?? []).some((r) => r.value === reasonKind && (r.rider === null || r.rider === k))) setReasonKind('')
    if (k === 'owner' && carriedBy === 'driver') setCarriedBy('fleetly')
  }

  function chooseDriver(id: string) {
    setDriverId(id)
    const d = drivers.find((x) => x.id === id)
    if (d?.last_vehicle_id && allVehicles.some((v) => v.id === d.last_vehicle_id)) setVehicleId(d.last_vehicle_id)
  }

  function chooseOwner(id: string) {
    setOwnerId(id)
    const o = owners.find((x) => x.id === id)
    setVehicleId(o && o.vehicle_ids.length === 1 ? o.vehicle_ids[0] : '')
  }

  async function save() {
    setError(null)
    try {
      const r = await create.trigger({
        rider_kind: kind,
        driver_id: kind === 'driver' ? driverId || null : null,
        owner_id: kind === 'owner' ? ownerId || null : null,
        vehicle_id: vehicleId || null,
        reason_kind: reasonKind || null,
        reason,
        pickup,
        dropoff,
        ride_at: rideAt || null,
        amount_zar: amount.replace(',', '.').trim(),
        uber_trip_ref: tripRef,
        carried_by: carriedBy,
        carried_by_note: carriedBy === 'fleetly' ? null : carriedNote,
      })
      if (r) { setSaved(r); onSaved() }
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e))
    }
  }

  if (saved) {
    return (
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>{saved.ref} is logged</DialogTitle>
            <DialogDescription>
              If Uber's final charge differs from what you entered, open the ride and correct the fare from the receipt.
            </DialogDescription>
          </DialogHeader>
          <div className="flex justify-end"><Button variant="brand" onClick={() => onOpenChange(false)}>Done</Button></div>
        </DialogContent>
      </Dialog>
    )
  }

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!create.loading) onOpenChange(o) }}>
      <DialogContent className="max-h-[92vh] max-w-lg overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Log an Uber ride</DialogTitle>
          <DialogDescription>
            Book it in the Uber app first, then log it here straight away so the cost is on the record.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3 text-sm">
          <fieldset className="space-y-2">
            <legend className="text-xs font-medium text-foreground">Who is riding</legend>
            <div className="flex flex-wrap gap-4 text-xs">
              <label className="flex items-center gap-2"><input type="radio" name="ur-kind" checked={kind === 'driver'} onChange={() => chooseKind('driver')} /> A driver</label>
              <label className="flex items-center gap-2"><input type="radio" name="ur-kind" checked={kind === 'owner'} onChange={() => chooseKind('owner')} /> An owner</label>
            </div>
            {kind === 'driver' ? (
              <NativeSelect id="ur-driver" aria-label="Driver" value={driverId} onChange={(e) => chooseDriver(e.target.value)}>
                <option value="">Choose the driver</option>
                {drivers.map((d) => <option key={d.id} value={d.id}>{d.name}{d.status !== 'active' ? ` (${d.status.replace('_', ' ')})` : ''}</option>)}
              </NativeSelect>
            ) : (
              <NativeSelect id="ur-owner" aria-label="Owner" value={ownerId} onChange={(e) => chooseOwner(e.target.value)}>
                <option value="">Choose the owner</option>
                {owners.map((o) => <option key={o.id} value={o.id}>{o.name}{o.status !== 'active' ? ` (${o.status})` : ''}</option>)}
              </NativeSelect>
            )}
          </fieldset>

          <div className="space-y-1">
            <label htmlFor="ur-reason-kind" className="text-xs font-medium text-foreground">Why the ride is needed</label>
            <NativeSelect id="ur-reason-kind" value={reasonKind} onChange={(e) => setReasonKind(e.target.value)}>
              <option value="">Choose the reason</option>
              {reasons.map((r) => <option key={r.value} value={r.value}>{r.label}</option>)}
            </NativeSelect>
            <Input aria-label="Details" placeholder="e.g. Car at Toyota Rosebank for its 60,000 km service" value={reason} onChange={(e) => setReason(e.target.value)} />
          </div>

          <div className="space-y-1">
            <label htmlFor="ur-vehicle" className="text-xs font-medium text-foreground">Car {carriedBy === 'owner' ? '' : '(optional)'}</label>
            <NativeSelect id="ur-vehicle" value={vehicleId} onChange={(e) => setVehicleId(e.target.value)}>
              <option value="">{kind === 'owner' && owner && owner.vehicle_ids.length === 0 ? 'This owner has no car on record' : 'No car, or not about a car'}</option>
              {vehicles.map((v) => <option key={v.id} value={v.id}>{v.label}</option>)}
            </NativeSelect>
            {kind === 'driver' && driverId && <p className="text-[11px] text-muted-foreground">Filled with the car they last drove. Change it if the ride is about another one.</p>}
          </div>

          <div className="grid gap-3 sm:grid-cols-2">
            <div className="space-y-1">
              <label htmlFor="ur-pickup" className="text-xs font-medium text-foreground">From</label>
              <Input id="ur-pickup" placeholder="Pickup address or area" value={pickup} onChange={(e) => setPickup(e.target.value)} />
            </div>
            <div className="space-y-1">
              <label htmlFor="ur-dropoff" className="text-xs font-medium text-foreground">To</label>
              <Input id="ur-dropoff" placeholder="Drop-off address or area" value={dropoff} onChange={(e) => setDropoff(e.target.value)} />
            </div>
            <div className="space-y-1">
              <label htmlFor="ur-at" className="text-xs font-medium text-foreground">Date and time of the ride</label>
              <Input id="ur-at" type="datetime-local" value={rideAt} onChange={(e) => setRideAt(e.target.value)} />
            </div>
            <div className="space-y-1">
              <label htmlFor="ur-amount" className="text-xs font-medium text-foreground">Fare (R)</label>
              <Input id="ur-amount" inputMode="decimal" placeholder="185.40" value={amount} onChange={(e) => setAmount(e.target.value)} />
            </div>
          </div>
          <div className="space-y-1">
            <label htmlFor="ur-trip" className="text-xs font-medium text-foreground">Uber trip link or receipt number (optional)</label>
            <Input id="ur-trip" placeholder="Paste the trip link from the Uber app" value={tripRef} onChange={(e) => setTripRef(e.target.value)} />
          </div>

          <fieldset className="space-y-2 rounded-md border border-border p-3">
            <legend className="px-1 text-xs font-medium text-foreground">Who carries the cost</legend>
            <div className="flex flex-wrap gap-4 text-xs">
              <label className="flex items-center gap-2"><input type="radio" name="ur-carried" checked={carriedBy === 'fleetly'} onChange={() => setCarriedBy('fleetly')} /> Fleetly</label>
              {kind === 'driver' && (
                <label className="flex items-center gap-2"><input type="radio" name="ur-carried" checked={carriedBy === 'driver'} onChange={() => setCarriedBy('driver')} /> The driver</label>
              )}
              <label className="flex items-center gap-2"><input type="radio" name="ur-carried" checked={carriedBy === 'owner'} onChange={() => setCarriedBy('owner')} /> The car's owner</label>
            </div>
            {carriedBy === 'owner' && (
              <p className="text-[11px] text-muted-foreground">
                {carOwnerName ? `Recorded against ${carOwnerName}, who owns this car.` : 'Choose the car above; the cost is recorded against whoever owns it.'}
              </p>
            )}
            {carriedBy !== 'fleetly' && (
              <>
                <Input aria-label="Why not Fleetly" placeholder={`Why the ${carriedBy === 'driver' ? 'driver' : 'owner'} carries it rather than Fleetly`} value={carriedNote} onChange={(e) => setCarriedNote(e.target.value)} />
                <p className="text-[11px] text-muted-foreground">
                  This records who is expected to carry it. It is not yet deducted from pay or put on an owner statement; that is switched on separately.
                </p>
              </>
            )}
          </fieldset>

          {error && <p className="rounded-md bg-rose-50 px-3 py-2 text-xs text-rose-800 dark:bg-rose-950 dark:text-rose-200">{error}</p>}

          <div className="flex justify-end gap-2">
            <Button variant="ghost" onClick={() => onOpenChange(false)} disabled={create.loading}>Cancel</Button>
            <Button variant="brand" onClick={() => void save()} disabled={create.loading}>
              {create.loading ? <Spinner className="mr-1 h-4 w-4" /> : <Route className="mr-1 h-4 w-4" />} Log the ride
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  )
}
